import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CategoryIcon from '@mui/icons-material/Category';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../components/auth/AuthProvider';

const navigation = [
  { name: 'Dashboard', href: '/', icon: DashboardIcon },
  { name: 'Utilisateurs', href: '/users', icon: PeopleIcon },
  { name: 'Rôles & Permissions', href: '/roles', icon: SecurityIcon },
  { name: 'Boutiques', href: '/shops', icon: StorefrontIcon },
  { name: 'Catégories', href: '/categories', icon: CategoryIcon },
  { name: 'Collections', href: '/collections', icon: CollectionsBookmarkIcon },
  { name: 'Messagerie', href: '/messaging', icon: MailOutlineIcon },
  { name: 'Pré-inscriptions', href: '/waitlist', icon: HowToRegIcon },
  { name: 'Analytics', href: '/analytics', icon: AnalyticsIcon },
  { name: 'Paramètres', href: '/settings', icon: SettingsIcon },
];

const Sidebar = () => {
  const location = useLocation();
  const { admin, logout } = useAuth();

  // Génère les initiales à partir de l'email ou du département
  const getInitials = () => {
    if (admin?.department) {
      return admin.department.substring(0, 2).toUpperCase();
    }
    if (admin?.email) {
      return admin.email.substring(0, 2).toUpperCase();
    }
    return 'SA';
  };

  // Affiche un nom lisible
  const getDisplayName = () => {
    if (admin?.department) {
      return `Admin ${admin.department}`;
    }
    return 'Super Admin';
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <aside className="sidebar">
      {/* Logo */}
      <div className="px-6 py-6 border-b border-surface-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-500 to-accent-700 flex items-center justify-center shadow-glow">
            <span className="text-white font-bold text-lg">P</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Platform</h1>
            <p className="text-xs text-surface-500">Super Admin</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={isActive ? 'sidebar-item-active' : 'sidebar-item'}
              >
                <Icon className="w-5 h-5" />
                <span>{item.name}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-surface-800">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center">
            <span className="text-white text-sm font-medium">{getInitials()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-surface-100 truncate">{getDisplayName()}</p>
            <p className="text-xs text-surface-500 truncate">{admin?.email || 'admin@platform.com'}</p>
          </div>
          <button 
            onClick={handleLogout}
            title="Se déconnecter"
            className="p-2 text-surface-400 hover:text-red-400 hover:bg-surface-800 rounded-lg transition-colors"
          >
            <LogoutIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </aside>
  );
};

const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen">
      <Sidebar />
      
      {/* Main content */}
      <main className="ml-64 min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-surface-950/80 backdrop-blur-xl border-b border-surface-800 flex items-center px-8">
          <div className="flex-1">
            {/* Breadcrumb ou titre dynamique */}
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                className="w-64 px-4 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-accent-500/50 focus:border-accent-500"
              />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs text-surface-500 bg-surface-800 rounded">
                ⌘K
              </kbd>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
